import { getPlatformConfig } from "@/lib/platform/config";

export type NavigationLink = {
  href: string;
  /** One English publication, never prefixed with the viewer's country. */
  global?: boolean;
  labelKey: string;
  fallback: string;
  external?: boolean;
  featureFlag?: "feature_pro_owner" | "feature_referrals";
};

export type NavigationGroup = {
  id: string;
  labelKey: string;
  fallback: string;
  links: NavigationLink[];
};

export type PrimaryNavigationItem = NavigationLink & {
  id: string;
  children?: NavigationLink[];
};

const fixedExternalDestinations = getPlatformConfig("driveshare");

export const OCCASION_NAV_LINKS: NavigationLink[] = [
  { href: "/category/weddings", labelKey: "footer.weddings", fallback: "Weddings" },
  { href: "/guides/category/occasions", labelKey: "footer.celebrations_gifts", fallback: "Celebrations & Gifts" },
  { href: "/category/driving-tours", labelKey: "footer.driving_tours", fallback: "Driving Tours" },
  { href: "/category/scenic-routes", labelKey: "footer.scenic_routes", fallback: "Scenic Routes" },
  { href: "/category/foodie-drives", labelKey: "footer.foodie_drives", fallback: "Foodie Drives" },
  { href: "/category/weekend-getaways", labelKey: "footer.weekend_getaways", fallback: "Weekend Getaways" },
  { href: "/concierge", labelKey: "footer.concierge", fallback: "Concierge" },
];

export const PROFESSIONAL_NAV_LINKS: NavigationLink[] = [
  { href: "/hire/film-and-tv-car-hire", labelKey: "footer.film_tv", fallback: "Film & TV" },
  {
    href: "/category/photography",
    labelKey: "footer.photoshoots_social_advertising",
    fallback: "Photoshoots, Social Media & Advertising",
  },
  { href: "/category/corporate-events", labelKey: "footer.corporate_events", fallback: "Corporate Events" },
  { href: "/concierge", labelKey: "footer.concierge", fallback: "Concierge" },
];

export const COMPANY_HELP_LINKS: NavigationLink[] = [
  { href: "/bookaclassicisnowdriveshare", global: true, labelKey: "footer.brand_transition", fallback: "BookAclassic is now DRIVESHARE" },
  { href: "/help", labelKey: "footer.help_centre", fallback: "Help Centre" },
  { href: "/about", labelKey: "footer.about_us", fallback: "About Us" },
  { href: "/company", labelKey: "footer.company_link", fallback: "Company" },
  { href: "/guides", labelKey: "footer.guides", fallback: "Guides" },
  { href: "/apex-score", global: true, labelKey: "footer.apex_score", fallback: "APEX Score" },
  { href: "/models", labelKey: "footer.models", fallback: "Classic Car Models" },
  { href: "/cities", labelKey: "footer.cities", fallback: "Hire by City" },
  { href: "/compare", labelKey: "footer.compare", fallback: "Compare Classic Cars" },
  { href: fixedExternalDestinations.blogUrl, labelKey: "footer.blog", fallback: "Blog", external: true },
];

export const OWNER_NAV_LINKS: NavigationLink[] = [
  { href: "/list-a-car", labelKey: "nav.list_your_car", fallback: "List your car" },
  { href: "/why-list", labelKey: "footer.why_list", fallback: "Why list?" },
  { href: "/pro-owner", labelKey: "footer.pro_owner", fallback: "Pro Owner", featureFlag: "feature_pro_owner" },
  { href: "/refer-a-host", labelKey: "footer.refer_a_host", fallback: "Refer a Host", featureFlag: "feature_referrals" },
];

export const PRIMARY_NAVIGATION: PrimaryNavigationItem[] = [
  {
    id: "rent",
    href: "/search",
    labelKey: "nav.rent_a_car",
    fallback: "Rent a car",
  },
  {
    id: "hire",
    href: "/hire",
    labelKey: "nav.hire",
    fallback: "Hire a Classic Car",
  },
  {
    id: "owner",
    href: "/list-a-car",
    labelKey: "nav.list_your_car",
    fallback: "List your car",
  },
  {
    id: "help",
    href: "/help",
    labelKey: "nav.help_centre",
    fallback: "Help Centre",
  },
];

/** Footer discovery groups stay separate from the compact header navigation. */
export const PUBLIC_NAVIGATION_GROUPS: NavigationGroup[] = [
  {
    id: "renters",
    labelKey: "footer.renters_heading",
    fallback: "For renters",
    links: [
      { href: "/search", labelKey: "nav.rent_a_car", fallback: "Rent a car" },
      { href: "/hire", labelKey: "footer.hire", fallback: "Hire a Classic Car" },
      { href: "/how-it-works", labelKey: "footer.how_it_works", fallback: "How It Works" },
      ...OCCASION_NAV_LINKS,
    ],
  },
  {
    id: "owners",
    labelKey: "footer.owners_heading",
    fallback: "For owners",
    links: OWNER_NAV_LINKS,
  },
  {
    id: "professionals",
    labelKey: "footer.professionals_events_heading",
    fallback: "Professionals & Events",
    links: [
      { href: "/professional", labelKey: "footer.for_professionals", fallback: "For Professionals" },
      ...PROFESSIONAL_NAV_LINKS,
      { href: fixedExternalDestinations.eventsUrl, labelKey: "footer.events", fallback: "Events", external: true },
      { href: new URL("/weddings", fixedExternalDestinations.eventsUrl).href, labelKey: "footer.wedding_events_venue", fallback: "Wedding Events & Venue", external: true },
    ],
  },
  {
    id: "help-company",
    labelKey: "footer.help_company_heading",
    fallback: "Help & Company",
    links: COMPANY_HELP_LINKS,
  },
];
